import { createContext, useState, useEffect, ReactNode, useContext as useReactContext } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest } from '@/lib/queryClient';
import { useQuery } from '@tanstack/react-query';

export interface LocationData {
  selectedCity: string;
  selectedState: string | null;
  selectedStateCode: string | null;
  selectedPlaceId: string | null;
}

interface LocationContextValue {
  selectedLocation: LocationData | null;
  setSelectedLocation: (location: LocationData) => Promise<void>;
  clearSelectedLocation: () => Promise<void>;
  isLoading: boolean;
}

export const LocationContext = createContext<LocationContextValue | undefined>(undefined);

const LOCATION_STORAGE_KEY = 'trainingbooker_selected_location';

interface LocationProviderProps {
  children: ReactNode;
}

export function LocationProvider({ children }: LocationProviderProps) {
  const [selectedLocation, setSelectedLocationState] = useState<LocationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  // Load location preference on mount
  useEffect(() => {
    const loadLocation = async () => {
      try {
        setIsLoading(true);

        // Always check localStorage first for immediate availability
        const storedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
        let localLocation: LocationData | null = null;
        
        if (storedLocation) {
          try {
            localLocation = JSON.parse(storedLocation) as LocationData;
            setSelectedLocationState(localLocation);
          } catch (error) {
            console.error('Failed to parse stored location:', error);
            localStorage.removeItem(LOCATION_STORAGE_KEY);
          }
        }

        // If authenticated, fetch from API and sync
        if (isAuthenticated && user) {
          try {
            const response = await fetch('/api/user/location', {
              headers: {
                'Authorization': `Bearer ${await user.getIdToken()}`
              }
            });

            if (response.ok) {
              const apiLocation = await response.json();
              
              // If API has a location, use it and update localStorage
              if (apiLocation.selectedCity) {
                const location: LocationData = {
                  selectedCity: apiLocation.selectedCity,
                  selectedState: apiLocation.selectedState,
                  selectedStateCode: apiLocation.selectedStateCode,
                  selectedPlaceId: apiLocation.selectedPlaceId
                };
                
                setSelectedLocationState(location);
                localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
              } else if (localLocation && localLocation.selectedCity) {
                // Guest→signed-in transition: Sync localStorage preference to database
                console.log('🔄 Syncing guest location preference to database:', localLocation);
                
                try {
                  await apiRequest('PUT', '/api/user/location', localLocation);
                  
                  console.log('✅ Guest location preference synced to database');
                } catch (syncError) {
                  console.error('Failed to sync guest location to database:', syncError);
                  // Continue anyway - localStorage is still valid
                }
              }
            }
          } catch (error) {
            console.error('Failed to fetch location from API:', error);
            // Continue using localStorage value if API fails
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadLocation();
  }, [isAuthenticated, user]);

  // Set location preference
  const setSelectedLocation = async (location: LocationData) => {
    try {
      // Update state immediately for optimistic UI
      setSelectedLocationState(location);
      
      // Save to localStorage immediately
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));

      // If authenticated, save to API
      if (isAuthenticated && user) {
        try {
          await apiRequest('PUT', '/api/user/location', location);
          
          console.log('✅ Location preference saved to database');
        } catch (error) {
          console.error('Failed to save location to API:', error);
          // Continue anyway - localStorage is updated
        }
      }
    } catch (error) {
      console.error('Failed to set location preference:', error);
      throw error;
    }
  };

  // Clear location preference
  const clearSelectedLocation = async () => {
    try {
      // Clear state immediately
      setSelectedLocationState(null);
      
      // Clear localStorage immediately
      localStorage.removeItem(LOCATION_STORAGE_KEY);

      // If authenticated, clear from API
      if (isAuthenticated && user) {
        try {
          await apiRequest('PUT', '/api/user/location', {
            selectedCity: null,
            selectedState: null,
            selectedStateCode: null,
            selectedPlaceId: null
          });
          
          console.log('✅ Location preference cleared from database');
        } catch (error) {
          console.error('Failed to clear location from API:', error);
          // Continue anyway - localStorage is cleared
        }
      }
    } catch (error) {
      console.error('Failed to clear location preference:', error);
      throw error;
    }
  };

  const value: LocationContextValue = {
    selectedLocation,
    setSelectedLocation,
    clearSelectedLocation,
    isLoading
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

// Custom hook to get the selected city as a slug
export function useSelectedCitySlug() {
  const context = useReactContext(LocationContext);
  
  if (!context) {
    throw new Error('useSelectedCitySlug must be used within LocationProvider');
  }
  
  const { selectedLocation, isLoading: contextLoading } = context;
  
  // Only query if we have a selected city
  const { data, isLoading: queryLoading } = useQuery({
    queryKey: ['/api/cities/by-name', selectedLocation?.selectedCity, selectedLocation?.selectedStateCode],
    queryFn: async () => {
      if (!selectedLocation?.selectedCity) return null;
      
      const url = `/api/cities/by-name/${encodeURIComponent(selectedLocation.selectedCity)}${
        selectedLocation.selectedStateCode ? `?stateCode=${selectedLocation.selectedStateCode}` : ''
      }`;
      
      console.log('🔍 useSelectedCitySlug FETCHING:', {
        selectedCity: selectedLocation.selectedCity,
        stateCode: selectedLocation.selectedStateCode,
        url
      });
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.log('❌ useSelectedCitySlug FAILED:', {
          selectedCity: selectedLocation.selectedCity,
          status: response.status
        });
        return null;
      }
      
      const data = await response.json();
      console.log('✅ useSelectedCitySlug SUCCESS:', {
        selectedCity: selectedLocation.selectedCity,
        slug: data.slug
      });
      
      return data;
    },
    enabled: !!selectedLocation?.selectedCity && !contextLoading,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour - city names don't change
    retry: false
  });
  
  return {
    citySlug: data?.slug || null,
    isResolving: contextLoading || queryLoading,
    selectedCity: selectedLocation?.selectedCity || null
  };
}
